/**
 * Lectura local de un VIN de 17 caracteres (sin llamadas): fabricante y país por el
 * prefijo (WMI), año modelo por el 10.º carácter y dígito verificador (9.º). Sirve
 * para cruzarlo con la marca y el año que indicó el analista.
 */

/** Fabricantes frecuentes en Chile por prefijo (3 caracteres, o 2 si alcanza). */
const WMI: Record<string, string> = {
  JT: "Toyota", JN: "Nissan", JM: "Mazda", JH: "Honda", JS: "Suzuki", JF: "Subaru", JD: "Daihatsu",
  JA3: "Mitsubishi", JA4: "Mitsubishi", JMB: "Mitsubishi", JMY: "Mitsubishi", JAA: "Isuzu", JAL: "Isuzu",
  KMH: "Hyundai", KMF: "Hyundai", KMJ: "Hyundai", KNA: "Kia", KNC: "Kia", KND: "Kia", KNE: "Kia", KL: "Chevrolet", KPT: "SsangYong",
  MA3: "Suzuki", MAL: "Hyundai", MAJ: "Ford", MAT: "Tata", MR0: "Toyota", MR1: "Toyota", MR2: "Toyota", MHF: "Toyota",
  MM8: "Mazda", MMB: "Mitsubishi", MMC: "Mitsubishi", MNT: "Nissan", MNB: "Ford", MPA: "Isuzu",
  LVV: "Chery", LGW: "Great Wall", LGX: "BYD", LSJ: "MG", LB3: "Geely", L6T: "Geely", LJ1: "JAC", LZW: "Chevrolet",
  LNB: "BAIC", LVS: "Ford", LS5: "Changan", LDC: "Dongfeng", LGB: "Dongfeng",
  "9BW": "Volkswagen", "9BG": "Chevrolet", "9BD": "Fiat", "9BF": "Ford", "93Y": "Renault", "936": "Peugeot", "935": "Citroën",
  "9BR": "Toyota", "8AJ": "Toyota", "8AP": "Fiat", "8AF": "Ford", "8AG": "Chevrolet", "8AW": "Volkswagen", "8AD": "Peugeot", "8A1": "Renault",
  "3N1": "Nissan", "3N6": "Nissan", "3VW": "Volkswagen", "3MZ": "Mazda", "3MV": "Mazda", "3MD": "Mazda", "3FA": "Ford", "3FT": "Ford",
  "3G": "Chevrolet", "3HG": "Honda", "3KP": "Kia", "3TM": "Toyota",
  "1F": "Ford", "1G": "Chevrolet", "1C": "Chrysler", "1J": "Jeep", "1H": "Honda", "1N": "Nissan", "4T": "Toyota", "5T": "Toyota",
  "2T": "Toyota", "2H": "Honda", "2G": "Chevrolet", "2C": "Chrysler",
  WVW: "Volkswagen", WV1: "Volkswagen", WV2: "Volkswagen", WBA: "BMW", WBS: "BMW", WBY: "BMW", WDB: "Mercedes-Benz",
  WDD: "Mercedes-Benz", WDC: "Mercedes-Benz", WDF: "Mercedes-Benz", W1K: "Mercedes-Benz", W1N: "Mercedes-Benz", WAU: "Audi",
  WUA: "Audi", WP0: "Porsche", WP1: "Porsche", W0L: "Opel", WF0: "Ford",
  VF1: "Renault", VF3: "Peugeot", VR3: "Peugeot", VF7: "Citroën", VSS: "SEAT", VNK: "Toyota",
  YV1: "Volvo", ZFA: "Fiat", ZAR: "Alfa Romeo", SAL: "Land Rover", SAJ: "Jaguar", SJN: "Nissan", TMB: "Škoda", TSM: "Suzuki", UU1: "Dacia",
};

function country(vin: string): string | undefined {
  const [a = "", b = ""] = vin;
  if (a === "J") return "Japón";
  if (a === "K") return "Corea del Sur";
  if (a === "L") return "China";
  if (a === "M") return "ABCDE".includes(b) ? "India" : "FGHIJK".includes(b) ? "Indonesia" : "Tailandia";
  if ("145".includes(a)) return "Estados Unidos";
  if (a === "2") return "Canadá";
  if (a === "3") return "México";
  if (a === "8") return "ABCDE".includes(b) ? "Argentina" : undefined;
  if (a === "9") return "ABCDE3456789".includes(b) ? "Brasil" : undefined;
  if (a === "W") return "Alemania";
  if (a === "V") return "STUVW".includes(b) ? "España" : "Francia";
  if (a === "Z") return "Italia";
  if (a === "S") return "Reino Unido";
  if (a === "Y") return "Suecia";
  return undefined;
}

const YEAR_CODES = "ABCDEFGHJKLMNPRSTVWXY123456789";
const TRANSLIT: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

export interface VinReading {
  maker?: string;
  country?: string;
  /** Año modelo del 10.º carácter (el ciclo de 30 años más reciente que no pasa del año siguiente). */
  modelYear?: number;
  /** ¿El 9.º carácter calza con el cálculo? (obligatorio solo en vehículos para Norteamérica). */
  checkOk: boolean;
}

export function readVin(raw: string, now = new Date()): VinReading | null {
  const vin = raw.trim().toUpperCase();
  if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) return null;
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const c = vin[i]!;
    sum += (/\d/.test(c) ? Number(c) : TRANSLIT[c]!) * WEIGHTS[i]!;
  }
  const check = sum % 11 === 10 ? "X" : String(sum % 11);
  const out: VinReading = { checkOk: vin[8] === check };
  const maker = WMI[vin.slice(0, 3)] ?? WMI[vin.slice(0, 2)];
  if (maker) out.maker = maker;
  const c = country(vin);
  if (c) out.country = c;
  const idx = YEAR_CODES.indexOf(vin[9]!);
  if (idx >= 0) {
    const max = now.getFullYear() + 1;
    const year = [2010 + idx, 1980 + idx].find((y) => y <= max);
    if (year !== undefined) out.modelYear = year;
  }
  return out;
}

const fold = (s: string): string => s.normalize("NFD").replace(/\p{M}+/gu, "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Lo que el VIN dice, para mostrar junto a lo indicado: "Mazda (Japón) · año modelo 2019",
 * con avisos si la marca o el año no calzan con los del caso.
 */
export function vinSummary(raw: string, ctx: { make?: string; yearFrom?: number; yearTo?: number; now?: Date } = {}):
  { value: string; notes: string[]; mismatch: boolean } | null {
  const r = readVin(raw, ctx.now);
  if (!r) return null;
  const parts = [r.maker ? `${r.maker}${r.country ? ` (${r.country})` : ""}` : r.country, r.modelYear ? `año modelo ${r.modelYear}` : undefined]
    .filter(Boolean) as string[];
  const notes: string[] = [r.checkOk
    ? "dígito verificador correcto"
    : "el dígito verificador no calza (solo es obligatorio en vehículos fabricados para Norteamérica)"];
  let mismatch = false;
  if (r.maker && ctx.make && !fold(r.maker).includes(fold(ctx.make)) && !fold(ctx.make).includes(fold(r.maker))) {
    notes.push(`la marca del VIN (${r.maker}) no calza con la indicada`);
    mismatch = true;
  }
  if (r.modelYear && ctx.yearFrom !== undefined) {
    const from = ctx.yearFrom;
    const to = ctx.yearTo ?? from;
    if (r.modelYear < from - 1 || r.modelYear > to + 1) {
      notes.push(`el año modelo del VIN (${r.modelYear}) no calza con el indicado`);
      mismatch = true;
    } else if (r.modelYear < from || r.modelYear > to) {
      notes.push(`el año modelo del VIN (${r.modelYear}) difiere en uno del indicado: puede ser año de fabricación vs año modelo`);
    }
  }
  notes.push("no todos los fabricantes codifican el año en el VIN");
  return parts.length ? { value: parts.join(" · "), notes, mismatch } : { value: "fabricante no reconocido", notes, mismatch };
}
